import { useEffect, useMemo, useState } from 'react';
import { paiGowEvaluateFive, paiGowEvaluateTwo, paiGowIsLegalSplit, type PaiGowLegal, type PaiGowView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT, type MessageKey } from '../i18n';
import { useGame } from '../store/game';
import { MySeat } from './common';

const rankValue = (c: string): number => (c.startsWith('JK') ? 15 : '23456789TJQKA'.indexOf(c[0]!) + 2);
const byRank = (a: string, b: string): number => rankValue(b) - rankValue(a);

export function PaiGowTable() {
  const t = useT();
  const view = useGame((s) => s.view) as PaiGowView;
  const legal = useGame((s) => s.legal) as PaiGowLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const setting = Boolean(myTurn && legal?.kind === 'set');
  const [amount, setAmount] = useState<number>(view.config.minBet);
  const [low, setLow] = useState<string[]>([]);
  const ended = view.phase === 'HAND_END';
  const result = view.lastResult;
  const myResult = ended ? result?.seats.find((r) => r.seat === mySeat) : undefined;
  const cat = (c: string) => t(`pgw.cat.${c}` as MessageKey);

  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);

  // Each new deal starts from the house-way split, so a single tap on "Set hands" is enough.
  const suggestedKey = view.suggested ? view.suggested.low.join(',') : '';
  useEffect(() => {
    setLow(view.suggested ? [...view.suggested.low] : []);
  }, [view.handNo, suggestedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const hand = useMemo(() => [...view.hand].sort(byRank), [view.hand]);
  const high = hand.filter((c) => !low.includes(c));
  const ready = low.length === 2 && paiGowIsLegalSplit(high, low);
  const toggle = (c: string) => setLow((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : cur.length < 2 ? [...cur, c] : [cur[1]!, c]));

  const quick = (min: number, max: number) => [...new Set([1, 2, 5, 10, 25].map((k) => min * k).filter((v) => v <= max).concat(max))].sort((a, b) => a - b);
  const chip = (v: number, on: boolean, pick: () => void) => (
    <button key={v} role="radio" aria-checked={on} className={`bid-chip ${on ? 'on' : ''}`} onClick={pick}>
      {v}
    </button>
  );

  const row = (cards: string[], count: number, label: string, hidden = false) => (
    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', minHeight: 54 }} aria-label={label}>
      {Array.from({ length: count }, (_, i) => (cards[i] && !hidden ? <PlayingCard key={`${cards[i]}${i}`} id={cards[i]!} size="sm" /> : <PlayingCard key={i} id="AS" size="sm" faceDown />))}
    </div>
  );

  const dealtIn = view.players.some((p) => p && p.hasCards);
  const shownHigh = view.mySet?.high ?? high;
  const shownLow = view.mySet?.low ?? low;
  const lowCat = shownLow.length === 2 ? paiGowEvaluateTwo(shownLow).category : null;
  const highCat = shownHigh.length === 5 ? paiGowEvaluateFive(shownHigh).category : null;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer">
          <div className="card glass col" style={{ padding: 8, gap: 4, alignItems: 'center' }}>
            <strong>{t('pgw.dealer')}</strong>
            {ended && result ? (
              <>
                {row(result.dealerHigh, 5, t('pgw.high'))}
                {row(result.dealerLow, 2, t('pgw.low'))}
                <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                  {cat(result.dealerHighCategory)} · {cat(result.dealerLowCategory)}
                </span>
              </>
            ) : dealtIn ? (
              row([], 7, t('pgw.dealer'), true)
            ) : (
              <span className="dim">–</span>
            )}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 8, minHeight: 28, flexWrap: 'wrap' }}>
            {ended && myResult && (
              <span className={`pill ${myResult.delta > 0 ? 'accent' : myResult.delta < 0 ? 'danger' : ''}`} role="status">
                {myResult.delta > 0 ? t('ab.youWon', { n: myResult.delta }) : myResult.delta < 0 ? t('ab.youLost', { n: -myResult.delta }) : t('bc.push')}
              </span>
            )}
            {ended && myResult && myResult.commission > 0 && <span className="pill">{t('pgw.commission', { n: myResult.commission })}</span>}
            {view.phase === 'DEALING' && <span className="center-note">{t('ab.dealing')}</span>}
            {view.phase === 'REVEAL' && <span className="center-note">{t('pgw.revealing')}</span>}
            {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
            {(view.phase === 'BETTING' || view.phase === 'SETTING') && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
          </div>
          {view.hand.length === 7 && (
            <div className="card glass col" style={{ padding: 8, gap: 6, alignItems: 'center' }}>
              {setting ? (
                <>
                  <strong>{t('pgw.pickLow')}</strong>
                  <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }} role="group" aria-label={t('pgw.yourHand')}>
                    {hand.map((c) => (
                      <PlayingCard key={c} id={c} size="sm" selected={low.includes(c)} onClick={() => toggle(c)} />
                    ))}
                  </div>
                </>
              ) : (
                <strong>{t('pgw.yourHand')}</strong>
              )}
              <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                {t('pgw.high')}
                {highCat ? ` · ${cat(highCat)}` : ''}
              </span>
              {row(shownHigh, 5, t('pgw.high'))}
              <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                {t('pgw.low')}
                {lowCat ? ` · ${cat(lowCat)}` : ''}
              </span>
              {row(shownLow, 2, t('pgw.low'))}
              {setting && low.length === 2 && !ready && (
                <span role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--fs-xs)' }}>
                  {t('pgw.foul')}
                </span>
              )}
              {ended && myResult && (
                <span className="row" style={{ gap: 6 }}>
                  <span className={`pill ${myResult.highWon ? 'accent' : 'danger'}`}>{t(myResult.highWon ? 'pgw.highWon' : 'pgw.highLost')}</span>
                  <span className={`pill ${myResult.lowWon ? 'accent' : 'danger'}`}>{t(myResult.lowWon ? 'pgw.lowWon' : 'pgw.lowLost')}</span>
                </span>
              )}
            </div>
          )}
          <div className="card glass col" style={{ padding: 8, gap: 4 }} aria-label={t('bc.bets')}>
            {view.players.map((p) => {
              if (!p || p.status !== 'BET') return null;
              const r = ended ? result?.seats.find((x) => x.seat === p.seat) : undefined;
              return (
                <div key={p.seat} className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', minWidth: 64 }}>{p.seat === mySeat ? t('cg.you') : name(p.seat)}</span>
                  <span className="pill" style={{ fontSize: 'var(--fs-xs)' }}>{p.bet}</span>
                  {!ended && p.isSet && <span className="pill accent" style={{ fontSize: 'var(--fs-xs)' }}>{t('pgw.isSet')}</span>}
                  {r && (
                    <span className={`pill ${r.delta > 0 ? 'accent' : r.delta < 0 ? 'danger' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>
                      {cat(r.highCategory)} · {cat(r.lowCategory)} · {r.delta > 0 ? `+${r.delta}` : r.delta}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('bj.placeBet')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick(legal.min, legal.max).map((v) => chip(v, amount === v, () => setAmount(v)))}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', amount })}>
                {t('pgw.bet', { n: amount })}
              </button>
            </div>
          </div>
        )}
        {setting && (
          <div className="action-bar" role="group" aria-label={t('pgw.pickLow')}>
            <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'houseWay' })}>
              {t('pgw.houseWay')}
            </button>
            <button className="btn primary grow" disabled={acting || !ready} onClick={() => void act({ type: 'set', low })}>
              {t('pgw.setHands')}
            </button>
          </div>
        )}
        {!betting && !setting && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' && !ended ? t('pgw.youBet', { n: me.bet }) : ended ? t('bj.handOver') : view.phase === 'BETTING' ? t('bj.betting') : view.phase === 'SETTING' ? t('pgw.wait') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
